import React, { useState, useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useStoreActions, usePrograms } from '@/store/useStore';

// Base validation schema
const baseFormSchema = {
  name: z.string().optional(),
  email: z.string().email('Please enter a valid email address').optional().or(z.literal('')),
  program: z.string().min(1, 'Program is required'),
  year: z.string().min(1, 'Academic year is required'),
  semester: z.string().min(1, 'Semester is required'),
  group: z.string().min(1, 'Group is required'),
};

// Create schema based on mode
const createFormSchema = (mode: 'create' | 'edit') => {
  if (mode === 'create') {
    return z.object({
      ...baseFormSchema,
      category: z.string().min(1, 'Program category is required'),
      department: z.string().optional(),
    });
  } else {
    return z.object({
      ...baseFormSchema,
      category: z.string().optional(),
      department: z.string().min(1, 'Department is required'),
    });
  }
};

export type StudentFormValues = {
  name?: string;
  email?: string;
  category?: string;
  department?: string;
  program: string;
  year: string;
  semester: string;
  group: string;
};
type SubmitStatus = 'none' | 'validating' | 'submitting' | 'success' | 'error';
type ValidationErrors = Record<string, string>;

interface UseStudentFormLogicProps {
  initialData?: Partial<StudentFormValues>;
  onSubmit?: (data: StudentFormValues) => Promise<void>;
  mode?: 'create' | 'edit';
}

export function useStudentFormLogic({ 
  initialData = {}, 
  onSubmit,
  mode = 'create' 
}: UseStudentFormLogicProps = {}) {
  const { initializeData } = useStoreActions();
  const allPrograms = usePrograms();

  // State management - initialize with initialData values
  const [selectedCategory, setSelectedCategory] = useState(() => initialData.category || '');
  const [selectedDepartment, setSelectedDepartment] = useState(() => initialData.department || '');
  const [selectedProgram, setSelectedProgram] = useState(() => initialData.program || '');
  const [selectedYear, setSelectedYear] = useState(() => initialData.year || '');
  const [selectedSemester, setSelectedSemester] = useState(() => initialData.semester || '');
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('none');
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [isDataLoading, setIsDataLoading] = useState(true);

  // Initialize data
  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted) {
        setIsDataLoading(true);
      }
      await initializeData();
      if (isMounted) {
        setIsDataLoading(false);
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, []); // Remove initializeData from deps to avoid loops

  // Create dynamic schema for current mode
  const currentSchema = createFormSchema(mode);
  
  // Form setup with memoized default values
  const defaultValues = React.useMemo(() => ({
    name: initialData.name || '',
    email: initialData.email || '',
    category: initialData.category || '',
    department: initialData.department || '',
    program: initialData.program || '',
    year: initialData.year || '',
    semester: initialData.semester || '',
    group: initialData.group || '',
  }), [initialData.name, initialData.email, initialData.category, initialData.department, initialData.program, initialData.year, initialData.semester, initialData.group]);

  const form = useForm<StudentFormValues>({
    resolver: zodResolver(currentSchema) as any,
    defaultValues,
    mode: 'onChange',
  });

  // Computed values for cascading dropdowns
  const availablePrograms = useMemo(() => {
    if (mode === 'create' && selectedCategory) {
      // For StudentForm - filter by category
      const filteredPrograms = allPrograms.filter(p => {
        const programName = p.program.toLowerCase();
        if (selectedCategory === 'bachelor') {
          // Include programs with "bachelor" or "bs" prefix
          // Exclude programs with "master", "ms", "mphil", "phd", or "mba"
          const isBachelor = programName.includes('bachelor') || 
                            programName.startsWith('bs ') || 
                            programName.startsWith('b.s') ||
                            programName.includes('(bs)');
          const isMaster = programName.includes('master') || 
                          programName.includes('ms ') || 
                          programName.includes('m.s') ||
                          programName.includes('(ms)') ||
                          programName.includes('mba') ||
                          programName.includes('mphil') ||
                          programName.includes('phd') ||
                          programName.includes('ph.d');
          return isBachelor && !isMaster;
        } else {
          // Master programs
          return programName.includes('master') || 
                 programName.startsWith('ms ') || 
                 programName.startsWith('m.s') ||
                 programName.includes('(ms)') ||
                 programName.includes('mba') ||
                 programName.includes('m.b.a') ||
                 programName.includes('mphil') ||
                 programName.includes('phd') ||
                 programName.includes('ph.d');
        }
      });
      return Array.from(new Set(filteredPrograms.map(p => p.program)));
    } else if (mode === 'edit' && selectedDepartment) {
      // For EditProfileModal - filter by department
      const filteredPrograms = allPrograms.filter(p => 
        p.department.toLowerCase() === selectedDepartment.toLowerCase()
      );
      return Array.from(new Set(filteredPrograms.map(p => p.program)));
    }
    return [];
  }, [selectedCategory, selectedDepartment, allPrograms, mode]);

  const availableYears = useMemo(() => {
    if (!selectedProgram) return [];
    const programs = allPrograms.filter(p => p.program === selectedProgram);
    return Array.from(new Set(programs.map(p => p.years)));
  }, [selectedProgram, allPrograms]);

  const availableSemesters = useMemo(() => {
    if (!selectedYear) return [];
    const programs = allPrograms.filter(p => 
      p.program === selectedProgram &&
      p.years === selectedYear
    );
    return Array.from(new Set(programs.map(p => p.semester)));
  }, [selectedProgram, selectedYear, allPrograms]);

  const availableGroups = useMemo(() => {
    if (!selectedSemester) return [];
    const programs = allPrograms.filter(p => 
      p.program === selectedProgram &&
      p.years === selectedYear &&
      p.semester === selectedSemester
    );
    return Array.from(new Set(programs.map(p => p.group)));
  }, [selectedProgram, selectedYear, selectedSemester, allPrograms]);

  // Advanced validation function
  const validateFormData = (data: StudentFormValues): ValidationErrors => {
    const errors: ValidationErrors = {};
    
    // Basic schema validation
    const result = currentSchema.safeParse(data);
    
    if (!result.success && result.error) {
      result.error.issues.forEach(error => {
        if (error.path[0]) {
          errors[error.path[0] as string] = error.message;
        }
      });
    }

    // Advanced availability validation
    if (data.program) {
      const isValidProgram = availablePrograms.includes(data.program);
      if (availablePrograms.length > 0 && !isValidProgram) {
        errors.program = mode === 'create' 
          ? 'Selected program is not available for this category'
          : 'Selected program is not available for this department';
      }
    }

    if (data.program && data.year) {
      const isValidYear = availableYears.includes(data.year);
      if (!isValidYear) {
        errors.year = 'Selected year is not available for this program';
      }
    }

    if (data.program && data.year && data.semester) {
      const isValidSemester = availableSemesters.includes(data.semester);
      if (!isValidSemester) {
        errors.semester = 'Selected semester is not available for this program and year';
      }
    }

    if (data.program && data.year && data.semester && data.group) {
      const isValidGroup = availableGroups.includes(data.group);
      if (!isValidGroup) {
        errors.group = 'Selected group is not available for this program, year, and semester';
      }
    }

    return errors;
  };

  // Clear specific error when user starts interacting with field
  const clearFieldError = (fieldName: string) => {
    setValidationErrors(prev => {
      const updated = { ...prev };
      delete updated[fieldName];
      return updated;
    });
  };

  // Real-time validation as user types/selects
  const handleFieldChange = (fieldName: string, value: string, formSetValue: any) => {
    clearFieldError(fieldName);
    formSetValue(fieldName, value);
    
    // Perform real-time validation after a short delay
    setTimeout(() => {
      const currentFormData = form.getValues();
      const errors = validateFormData({ ...currentFormData, [fieldName]: value });
      if (errors[fieldName]) {
        setValidationErrors(prev => ({ ...prev, [fieldName]: errors[fieldName] }));
      }
    }, 300);
  };

  // Handle form submission
  const handleFormSubmit = async (data: StudentFormValues) => {
    setSubmitStatus('validating');
    
    try {
      const errors = validateFormData(data);
      setValidationErrors(errors);
      
      if (Object.keys(errors).length > 0) {
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus('none'), 3000);
        return;
      }
      
      setSubmitStatus('submitting');
      
      if (onSubmit) {
        await onSubmit(data);
      }
      
      setSubmitStatus('success');
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('none'), 3000);
    }
  };

  // Reset functions for cascading dropdowns
  const resetDependentFields = (level: 'category' | 'department' | 'program' | 'year' | 'semester') => {
    const resetFields: string[] = [];
    
    switch (level) {
      case 'category':
      case 'department':
        resetFields.push('program', 'year', 'semester', 'group');
        setSelectedProgram('');
        setSelectedYear('');
        setSelectedSemester('');
        break;
      case 'program':
        resetFields.push('year', 'semester', 'group');
        setSelectedYear('');
        setSelectedSemester('');
        break;
      case 'year':
        resetFields.push('semester', 'group');
        setSelectedSemester('');
        break;
      case 'semester':
        resetFields.push('group');
        break;
    }
    
    resetFields.forEach(field => {
      form.setValue(field as keyof StudentFormValues, '');
      clearFieldError(field);
    });
  };

  // Helper function to get department for a program
  const getDepartmentForProgram = (programName: string): string => {
    const program = allPrograms.find(p => p.program === programName);
    return program?.department || '';
  };

  return {
    // Form
    form,
    handleSubmit: form.handleSubmit(handleFormSubmit),
    
    // State
    selectedCategory,
    setSelectedCategory,
    selectedDepartment, 
    setSelectedDepartment,
    selectedProgram,
    setSelectedProgram,
    selectedYear,
    setSelectedYear,
    selectedSemester,
    setSelectedSemester,
    submitStatus,
    validationErrors,
    isDataLoading,
    
    // Computed values
    availablePrograms,
    availableYears,
    availableSemesters,
    availableGroups,
    allPrograms,
    
    // Helper functions
    validateFormData,
    clearFieldError,
    handleFieldChange,
    resetDependentFields,
    getDepartmentForProgram,
  };
}